import * as React from 'react';
import { useState, useEffect, memo } from 'react';
import { ArrowRightCircleIcon } from '../Icons/ArrowRightCircleIcon';
import Slider from 'react-slick';
import styled from '@emotion/styled';
import { Button } from '../../components/Button/Button';
import { BreakpointsEnum } from '../../helpers/breakpoints';
import { keyframes } from '@emotion/react';

export interface IMediaItem {
    image?: string;
    video?: string;
    thumbnail?: string;
}
interface ICarouselProps {
    images: IMediaItem[] | string[];
    cruiseTitle?: string;
    isDesktop?: boolean;
    logo?: string;
    actionButtonText?: string;
    actionButtonHandler?: () => void;
    hasRouteImage?: boolean;
    showActionButtonFirstSlide?: boolean;
}

const DesktopArrow = styled.div`
    color: ${({ theme }) => theme.icon.brandColor1} !important;
    &:hover {
        color: ${({ theme }) => theme.colors.maastricht[100]} !important;
    }
`;

function NextArrow(props) {
    const { className, style, onClick } = props;
    return <DesktopArrow className={className} style={{ ...style }} onClick={onClick} />;
}

function PrevArrow(props) {
    const { className, style, onClick } = props;
    return <DesktopArrow className={className} style={{ ...style }} onClick={onClick} />;
}

interface IMobileArrowProps {
    left?: boolean;
    right?: boolean;
}

const MobileArrow = styled.div<IMobileArrowProps>`
    right: ${(props) => (props?.right ? '10px' : 'auto')};
    left: ${(props) => (props?.left ? '10px' : 'auto')};
    transform: translateY(-50%);
    top: 50%;
    position: absolute;
    z-index: 1;
    color: white !important;
`;

const LogoWrapper = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    width: 90;
    height: 60;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const fadeIn = keyframes`
  0%, 80%{
    opacity: 0;
  }

  100%{
    opacity: 1;
  }
`;

const ActionButtonWrapper = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    top: calc(100% - 48px);
    width: 100%;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    animation: ${fadeIn} 600ms linear;

    @media (min-width: ${BreakpointsEnum.md}) {
        top: calc(100% - 158px);
    }
`;

const Logo = styled.img`
    width: 80%;
`;

const youtubeParser = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : false;
};

export const mapMediaToGallery = (
    images,
    thumbnails,
    routeImage,
    video,
    videoThumbPaths,
): IMediaItem[] => {
    let mediaGallery: IMediaItem[] = images.map((img, i) => {
        return { image: img, thumbnail: thumbnails[i] };
    });

    const addAfter = (array, index, newItem) => {
        return [...array.slice(0, index), newItem, ...array.slice(index)];
    };

    let addAtIndex = 0;

    if (routeImage) {
        const routeImageItem = { image: routeImage, thumbnail: routeImage };
        mediaGallery = addAfter(mediaGallery, addAtIndex, routeImageItem);
        addAtIndex = addAtIndex + 2;
    }

    if (video.length > 0) {
        const isSameLength = video.length === videoThumbPaths?.length;
        video.forEach((vid, i) => {
            if (vid) {
                const youtubeCover = `https://img.youtube.com/vi/${youtubeParser(
                    vid,
                )}/hqdefault.jpg`;
                const youtubeCoverThumbnail = `https://img.youtube.com/vi/${youtubeParser(
                    vid,
                )}/2.jpg`;
                const routeImageItem: IMediaItem = {
                    image: youtubeCover,
                    video: vid,
                    thumbnail:
                        isSameLength && videoThumbPaths[i]?.length > 0
                            ? videoThumbPaths[i]
                            : youtubeCoverThumbnail,
                };
                mediaGallery = addAfter(mediaGallery, addAtIndex, routeImageItem);
                addAtIndex++;
            }
        });
    }

    return mediaGallery;
};

export const getRouteImage = (image: string, countryCode: string, cruiseId: number) => {
    return `https://maps-json.dreamlines.de/images/${countryCode}-${cruiseId}.png`;
};

const isIMediaItem = (images: IMediaItem[] | string[]): images is IMediaItem[] => {
    return (images as IMediaItem[])[0].image !== undefined;
};

const convertToIMediaItem = (images: string[]): IMediaItem[] => {
    return images.map((img) => {
        return { image: img, thumbnail: img };
    });
};

const Gallery = ({
    images,
    cruiseTitle,
    isDesktop = true,
    logo,
    actionButtonText,
    actionButtonHandler,
    hasRouteImage,
    showActionButtonFirstSlide = true,
}: ICarouselProps) => {
    const [nav1, setNav1] = useState(null);
    const [nav2, setNav2] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [activeVideoSlide, setActiveVideoSlide] = useState(null);
    const [isVideoPlay, setIsVideoPlay] = useState(false);
    const [errorMedias, setErrorMedias] = useState({});

    const isOnFirstSlide = currentSlideIndex === 0;
    const isActionButtonEnabled = actionButtonHandler && actionButtonText;
    const showActionButton = showActionButtonFirstSlide || !isOnFirstSlide;

    if (images.length <= 0) return null;

    const mediasTemp: IMediaItem[] = isIMediaItem(images) ? images : convertToIMediaItem(images);
    const medias = mediasTemp.filter((item) => !errorMedias[item.image]);

    const compName = 'product-gallery';
    const imgAlt = cruiseTitle?.length > 0 ? cruiseTitle : 'Dreamlines';

    const mobileArrows = !isDesktop
        ? {
              nextArrow: <MobileArrow right />,
              prevArrow: <MobileArrow left />,
          }
        : {};

    return (
        <section className={`${compName}`}>
            {logo && (
                <LogoWrapper>
                    <Logo src={logo} alt="Company Logo" />
                </LogoWrapper>
            )}
            {isActionButtonEnabled && showActionButton && !isVideoPlay && (
                <ActionButtonWrapper>
                    <Button color="tertiary" size="small" onClick={actionButtonHandler}>
                        {actionButtonText}
                    </Button>
                </ActionButtonWrapper>
            )}
            <div className={`${compName}__slider  ${isDesktop ? 'isDesktop' : ''}`}>
                <Slider
                    arrows={!isDesktop}
                    draggable={!isDesktop}
                    swipeToSlide={!isDesktop}
                    asNavFor={nav2}
                    ref={(slider1) => setNav1(slider1)}
                    lazyLoad="ondemand"
                    fade={isDesktop}
                    dots={false}
                    beforeChange={(current, next) => {
                        setCurrentSlideIndex(next);
                        setIsVideoPlay(false);
                        if (current && medias?.length && medias[current].video?.length)
                            setActiveVideoSlide(next);
                        if (next && medias?.length && medias[next].video?.length)
                            setActiveVideoSlide(next);

                        setActiveVideoSlide(null);
                    }}
                    {...mobileArrows}
                >
                    {medias.map((item, index) => {
                        return (
                            <div
                                className={`${compName}__image-holder ${
                                    isDesktop ? '' : 'isMobile'
                                }`}
                                key={`${compName}__slide-${index}`}
                            >
                                {item.video?.length > 0 ? (
                                    <VideoItem
                                        video={item.video}
                                        image={item.image}
                                        imgAlt={imgAlt}
                                        activeVideoSlide={activeVideoSlide}
                                        handleVideoPlay={setIsVideoPlay}
                                        compName={compName}
                                    />
                                ) : (
                                    <ImgItem
                                        className={`${compName}__image`}
                                        src={item.image}
                                        alt={imgAlt}
                                        style={{ objectFit: hasRouteImage ? 'fill' : 'cover' }}
                                        onError={() =>
                                            setErrorMedias({ ...errorMedias, [item.image]: true })
                                        }
                                    />
                                )}
                            </div>
                        );
                    })}
                </Slider>
            </div>
            {isDesktop && (
                <div className={`${compName}__pagination`}>
                    <Slider
                        asNavFor={nav1}
                        ref={(slider2) => setNav2(slider2)}
                        slidesToShow={3}
                        slidesToScroll={1}
                        swipeToSlide={true}
                        focusOnSelect={true}
                        lazyLoad="ondemand"
                        nextArrow={<NextArrow />}
                        prevArrow={<PrevArrow />}
                        draggable={true}
                        speed={600}
                        dots={false}
                        infinite={medias.length > 3}
                    >
                        {medias.map((item, index) => (
                            <div
                                className={`${compName}__thumbnail-holder`}
                                key={`${compName}__thumbnail-${index}`}
                            >
                                {item.video?.length > 0 && (
                                    <div className={`${compName}__thumbnail__play-btn-holder`}>
                                        <ArrowRightCircleIcon
                                            width="42"
                                            height="42"
                                            viewBox="0 0 24 24"
                                            className={`${compName}__thumbnail__play-btn`}
                                        />
                                    </div>
                                )}
                                <img
                                    className={`${compName}__thumbnail`}
                                    src={item.thumbnail ? item.thumbnail : item.image}
                                    alt={imgAlt}
                                />
                            </div>
                        ))}
                    </Slider>
                </div>
            )}
        </section>
    );
};

const ImgItem = styled.img`
    aspect-ratio: 1.5;
    width: 100%;
    height: auto;
`;

const VideoWrapper = styled.div`
    overflow: hidden;
    height: 250px;
    @media (min-width: ${BreakpointsEnum.xs}) {
        height: 512px;
    }
    @media (min-width: ${BreakpointsEnum.md}) {
        height: 348px;
    }

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`;

const VideoItem = ({ video, image, compName, activeVideoSlide, imgAlt, handleVideoPlay }) => {
    const [isCoverImg, setIsCoverImg] = useState(true);

    useEffect(() => {
        setIsCoverImg(true);
    }, [activeVideoSlide]);

    if (isCoverImg) {
        const handlePlay = () => {
            setIsCoverImg(false);
            handleVideoPlay(true);
        };
        return (
            <div onClick={handlePlay} className={`${compName}__cover-image`}>
                <span className={`${compName}__play-button`}>▶</span>
                <ImgItem className={`${compName}__image`} src={image} alt={imgAlt} />
            </div>
        );
    } else {
        return (
            <VideoWrapper>
                <iframe
                    className={`${compName}__youtube-embed`}
                    src={`${video}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                />
            </VideoWrapper>
        );
    }
};

export default memo(Gallery);
